import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiGet, apiPost, apiPut } from 'app/shared-components/util/restAPI';
import { startLoading, stopLoading } from 'app/store/utilSlice';

export const fetchAvailableProducts = createAsyncThunk(
  'products/fetchAvailableProducts',
  async ({scope} = {}, { dispatch }) => {
    try {
      dispatch(startLoading());
      const apiName = 'CoreAPI2';
      const path = scope ? `/x2/product/available?scope=${scope}` : '/x2/product/available';
      const options = { headers: {}, response: false };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const fetchMySubscriptions = createAsyncThunk(
  'products/fetchMySubscriptions',
  async (_, { dispatch }) => {
    try {
      dispatch(startLoading());
      const apiName = 'CoreAPI2';
      const path = '/x2/product/mysubscriptions';
      const options = { headers: {}, response: false };
      return await apiGet(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const purchaseProduct = createAsyncThunk(
  'products/purchaseProduct',
  async ({productId, billingPeriod, quantity, assignToEntity}, { dispatch }) => {
    try {
      dispatch(startLoading());
      const apiName = 'CoreAPI2';
      const path = `/x2/product/${productId}/purchase`;
      const options = {
        body: { billingPeriod, quantity, assignToEntity },
        headers: {},
        response: false
      };
      return await apiPost(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const cancelSubscription = createAsyncThunk(
  'products/cancelSubscription',
  async ({subscriptionId}, { dispatch }) => {
    try {
      dispatch(startLoading());
      const apiName = 'CoreAPI2';
      const path = '/x2/product/cancel';
      const options = {
        body: { subscriptionId },
        headers: {},
        response: false
      };
      return await apiPost(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateSubscription = createAsyncThunk(
  'products/updateSubscription',
  async ({subscriptionId, quantity}, { dispatch }) => {
    try {
      dispatch(startLoading());
      const apiName = 'CoreAPI2';
      const path = '/x2/product/subscription';
      const options = {
        body: { subscriptionId, quantity },
        headers: {},
        response: false
      };
      return await apiPut(apiName, path, options);
    } finally {
      dispatch(stopLoading());
    }
  }
);

const productSlice = createSlice({
  name: 'products',
  initialState: {
    availableProducts: [],
    mySubscriptions: [],
    loading: false,
    purchaseConfirmDialogOpen: false,
    selectedProduct: null,
    error: null
  },
  reducers: {
    openPurchaseConfirmDialog: (state, action) => {
      state.purchaseConfirmDialogOpen = true;
      state.selectedProduct = action.payload;
    },
    closePurchaseConfirmDialog: (state) => {
      state.purchaseConfirmDialogOpen = false;
      state.selectedProduct = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAvailableProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableProducts.fulfilled, (state, action) => {
        state.availableProducts = action.payload;
        state.loading = false;
      })
      .addCase(fetchAvailableProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchMySubscriptions.fulfilled, (state, action) => {
        state.mySubscriptions = action.payload;
      })
      .addCase(purchaseProduct.fulfilled, (state, action) => {
        state.mySubscriptions.push(action.payload);
        state.purchaseConfirmDialogOpen = false;
        state.selectedProduct = null;
      })
      .addCase(cancelSubscription.fulfilled, (state, action) => {
        const index = state.mySubscriptions.findIndex(
          sub => sub.id === action.payload.id
        );
        if (index >= 0) {
          state.mySubscriptions[index] = action.payload;
        }
      })
      .addCase(updateSubscription.fulfilled, (state, action) => {
        const index = state.mySubscriptions.findIndex(
          sub => sub.id === action.payload.id
        );
        if (index >= 0) {
          state.mySubscriptions[index] = action.payload;
        }
      });
  }
});

export const selectAvailableProducts = ({ products }) => products.availableProducts;
export const selectMySubscriptions = ({ products }) => products.mySubscriptions;
export const selectProductsLoading = ({ products }) => products.loading;
export const selectPurchaseConfirmDialogOpen = ({ products }) => products.purchaseConfirmDialogOpen;
export const selectSelectedProduct = ({ products }) => products.selectedProduct;

export const { openPurchaseConfirmDialog, closePurchaseConfirmDialog } = productSlice.actions;
export default productSlice.reducer;